
import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { ToastClose, ToastHeader, ToastNotification, ToastText, ToastTitle } from "../Dashboard/DashboardElements";
import { DividerLine } from "../SwerlCollectionCard/CardElements";

// --- THEME DEFINITIONS ---
const THEME = {
  mainBg: "#080c16",
  secondaryBg: "#131a30",
  mainColor: "#FF6978",
  secondaryColor: "#b266ff",
  accentColor: "#868be3",
  secondaryColor: "#0d1324",
  successColor: "#05c483",
  warningColor: "#ffb74d",
  errorColor: "#ff4d4d",
};

// --- KEYFRAME ANIMATIONS ---
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(255, 105, 120, 0.2); }
  50% { box-shadow: 0 0 25px rgba(255, 105, 120, 0.4); }
`;

const rotateRecord = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const rippleRing = keyframes`
  0% { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
`;

// --- STYLED COMPONENTS ---
const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.mainBg};
  color: ${props => props.theme.secondaryBg};
  overflow-x: hidden;
`;

const SectionContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 42rem;
  margin: 0 auto 64px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.secondaryColor};
`;



const SectionDesc = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mainColor};
  line-height: 1.625;
  margin: 0;
`;

const SimulatorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: stretch;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
`;

const SimulatorLeft = styled.div`
  background-color: ${props => props.theme.secondaryBg};
  border: 1px solid #1e293b;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;

  @media (min-width: 1024px) {
    grid-column: span 4 / span 4;
  }
`;

const SimulatorRight = styled.div`
  background-color: ${props => props.theme.secondaryBg};
  border: 1px solid #1e293b;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;

  @media (min-width: 768px) {
    padding: 32px;
  }
  @media (min-width: 1024px) {
    grid-column: span 8 / span 8;
  }
`;

const BlockHeading = styled.h3`
  font-weight: 800;
  color: ${props => props.theme.secondaryBg};
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorIndicator = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color || props.theme.mainColor};
`;

const BoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBox = styled.div`
  background-color: ${props => props.theme.secondaryColor};
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
`;

const BoxLabel = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  margin: 0;
`;

const BoxValue = styled.h4`
  font-size: 0.875rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin-top: 4px;
  margin-bottom: 0;
  word-break: break-all;
`;

const BoxSub = styled.p`
  font-size: 10px;
  color: #64748b;
  margin-top: 2px;
  margin-bottom: 0;
`;

const OutlineButton = styled.button`
  width: 100%;
  background-color: ${props => props.theme.accentColor};
  color: ${props => props.theme.secondaryBg};
  font-weight: 700;
  padding: 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  border: 1px solid ${props => props.theme.secondaryBg};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.secondaryBg};
    color: ${props => props.theme.accentColor};
  }
`;

const BalanceHeader = styled.div`
  background-color: ${props => props.theme.secondaryColor};
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatusTag = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 900;
  padding: 4px 10px;
  border-radius: 9999px;
  background-color: ${props => props.active ? "rgba(5, 196, 131, 0.1)" : "rgba(255, 77, 77, 0.1)"};
  color: ${props => props.active ? props => props.theme.successColor : props => props.theme.errorColor};
  border: 1px solid ${props => props.active ? "rgba(5, 196, 131, 0.2)" : "rgba(255, 77, 77, 0.2)"};
`;

const CompactInputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const CompactInput = styled.input`
  flex: 1;
  background-color: ${props => props.theme.mainBg};
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryBg};

  &::placeholder {
    color: #475569;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.mainColor};
  }
`;

const CompactApplyButton = styled.button`
  background-color: ${props => props.theme.mainColor};
  color: ${props => props.theme.secondaryBg};
  font-weight: 700;
  padding: 0 16px;
  border-radius: 12px;
  font-size: 0.75rem;
  border: 1px solid ${props => props.theme.secondaryBg};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.secondaryBg};
    color: ${props => props.theme.mainColor};
  }
`;

const PlayerGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: center;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const VisualizerColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const DiscWrapper = styled.div`
  position: relative;
  width: 176px;
  height: 176px;
  border-radius: 50%;
  background-color: ${props => props.theme.secondaryColor};
  border: 4px solid #1e293b;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinningRing = styled.div`
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  border: 4px dashed rgba(255, 105, 120, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.spinning && css`
    animation: ${rotateRecord} 4s linear infinite;
  `}
`;

const InnerCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(255, 105, 120, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: ${props => props.theme.mainColor};
  font-size: 1.5rem;
`;

const DiscCenterPin = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.theme.mainBg};
  border: 1px solid #1e293b;
  z-index: 10;
`;

const PlayerMeta = styled.div`
  text-align: center;
`;

const TrackName = styled.h4`
  font-weight: 800;
  color: ${props => props.theme.secondaryBg};
  font-size: 1rem;
  margin: 0;
`;

const TrackAlbum = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.accentColor};
  margin-top: 4px;
  margin-bottom: 0;
`;

const TickerMetricsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LiveTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.theme.mainColor};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: block;
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const ProgressTrackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TrackLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: #64748b;
  font-weight: 700;
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${props => props.theme.secondaryColor};
  border: 1px solid #1e293b;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: linear-gradient(to right, ${props => props.theme.mainColor}, ${props => props.theme.secondaryColor});
  transition: width 1s linear;
  width: ${props => props.width || "0%"};
`;

const TerminalConsole = styled.div`
  background-color: #020617;
  border: 1px solid #1e293b;
  border-radius: 12px;
  padding: 12px;
  font-family: monospace;
  font-size: 10px;
  height: 100px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TerminalLine = styled.span`
  display: block;
  word-break: break-all;
  color: ${props => 
    props.type === "success" ? props => props.theme.successColor : 
    props.type === "warning" ? props => props.theme.warningColor : 
    props.type === "error" ? props => props.theme.errorColor : "${props.theme.accentColor}"
  };
`;

const PlayerBottom = styled.div`
  border-top: 1px solid #1e293b;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const AddressLabelBlock = styled.div`
  text-align: center;
  @media (min-width: 640px) {
    text-align: left;
  }
`;


const AddressHash = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.mainColor};
  font-family: monospace;
  font-weight: 700;
  margin-top: 4px;
  display: block;
`;

const PlayerButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  @media (min-width: 640px) {
    width: auto;
  }
`;

const PlayActionButton = styled.button`
  flex: 1;
  background-color: ${props => props.theme.mainColor};
  color: ${props => props.theme.mainBg};
  font-weight: 800;
  font-size: 0.875rem;
  padding: 14px 32px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 640px) {
    flex: none;
  }
`;

// --- ABOUT SECTION ---
const AboutSection = styled.section`
  padding: 80px 24px;
  max-width: 1280px;
  margin: 0 auto;
  scroll-margin-top: 80px;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
`;

const AboutLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    grid-column: span 5 / span 5;
  }
`;

const SectionSub = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.theme.mainColor};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: block;
`;

const AboutHeading = styled.h2`
  font-size: 1.875rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  line-height: 1.2;
  letter-spacing: -0.025em;

  @media (min-width: 640px) {
    font-size: 2.25rem;
  }
`;

const AboutDescPrimary = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryBg};
  line-height: 1.625;
  margin: 0;
`;

const AboutDescSecondary = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mainColor};
  line-height: 1.625;
  margin: 0;
`;

const AboutRight = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1024px) {
    grid-column: span 7 / span 7;
  }
`;



// --- PRICING SECTION ---
const PricingSection = styled.section`
  padding: 80px 0;
  background-color: ${props => props.theme.mainBg};
  border-top: 1px solid ${props => props.theme.secondaryBg};
  scroll-margin-top: 80px;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  align-items: stretch;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const PricingCard = styled.div`
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 24px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  position: relative;
  
  /* Ticket perforation cutouts for standard card styles */
  ${props => props.ticket && css`
    &::before, &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      background-color: ${props => props.theme.secondaryColor};
      border-radius: 50%;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
    }
    &::before { left: -8px; }
    &::after { right: -8px; }
  `}
`;

const RecommendedCard = styled(PricingCard)`
  background: linear-gradient(to bottom, ${props => props.theme.background}, ${props => props.theme.secondaryBg});
  color: ${props => props.theme.mainBg};
  border: 2px solid ${props => props.theme.mainColor};
`;

const RecommendBadge = styled.div`
  position: absolute;
  top: -14px;
  right: 24px;
  background-color: ${props => props.theme.mainColor};
  color: ${props => props.theme.mainBg};
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 12px;
  border-radius: 9999px;
`;

const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ListHeader = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryBg};
  font-weight: 700;
  display: block;
  margin-bottom: 8px;
`;

const FeatureList = styled.ul`
  font-size: 0.75rem;
  color: ${props => props.theme.mainColor};
  margin: 8px 0 0 0;
  padding-left: 16px;
  list-style-type: disc;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriceOptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: ${props => props.theme.secondaryBg};
  border: 1px solid ${props => props.theme.mainBg};
  color: ${props => props.theme.mainBg};
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.mainColor};
  }
`;

const OptionMetaBlock = styled.div``;

const OptionTitleText = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props =>  props.theme.mainBg};
  display: block;
`;

const OptionDiscountText = styled.span`
  font-size: 10px;
  color: ${props => props.highlight ? props => props.theme.mainColor : props => props.theme.secondaryColor};
  font-weight: 600;
  display: block;
`;

const OptionPriceText = styled.span`
  font-size: 0.75rem;
  font-weight: 900;
  color: ${props => props.highlight ? props => props.theme.mainColor : props => props.theme.mainBg};
`;


// --- SECTIONS LAYOUT ---
const CardSection = styled.section`
  padding: 80px 0;
  background-color: ${props => props.theme.mainBg};
  border-top: 1px solid ${props => props.theme.secondaryBg};
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  letter-spacing: -0.025em;
  margin: 0;

  @media (min-width: 640px) {
    font-size: 2.25rem;
  }
`;

const BodyDescPrimary = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.mainColor};
  line-height: 1.625;
  margin: 0;

  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const BodyDescSecondary = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.secondaryColor};
  line-height: 1.625;
  margin: 0;

  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const SideBadgeCard = styled.div`
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 16px;
  padding: 32px;
  position: relative;
  box-sizing: border-box;

  @media (min-width: 1024px) {
    grid-column: span 5 / span 5;
  }
`;

const FloatingBadgeIcon = styled.div`
  position: absolute;
  top: -16px;
  right: -16px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(255, 105, 120, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: ${props => props.theme.mainColor};
  border: 1px solid rgba(255, 105, 120, 0.3);
`;

const CardHeading = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.secondaryBg};
  margin: 0 0 16px 0;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.textSlate400};
  line-height: 1.625;
  margin: 0;
`;

const CalloutBanner = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: ${props => props.theme.accentColor};
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CalloutEmoji = styled.span`
  font-size: 1.5rem;
`;

const CalloutLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${props => props.theme.secondaryBg};
`;

// --- INTERACTIVE VISUALIZER ---
const CenterHeader = styled.div`
  text-align: center;
  max-width: 672px;
  margin: 0 auto 64px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PillarCard = styled.div`
    background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  padding: 24px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PillarIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || "rgba(255, 105, 120, 0.1)"};
  color: ${props => props.color || props.theme.mainColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const PillarTitle = styled.h4`
  font-weight: 800;
  color: ${props => props.theme.secondaryBg};
  font-size: 1rem;
  margin: 0;
`;

const PillarDesc = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.mainColor};
  line-height: 1.625;
  margin: 0;
`;

// --- SUBSCRIPTIONS TIERS ---

const SubCard = styled.div`
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  color: ${props => props.theme.secondaryBg};
  padding: 32px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
`;

const SubCardFeatured = styled(SubCard)`
  background: linear-gradient(to bottom, ${props => props.theme.background}, ${props => props.theme.secondaryBg});
  color: ${props => props.theme.mainBg}
  border: 2px solid ${props => props.theme.mainColor};
  position: relative;
`;

const SubCardTag = styled.div`
  position: absolute;
  top: -14px;
  right: 24px;
  background-color: ${props => props.theme.mainColor};
  color: ${props => props.theme.mainBg};
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 12px;
  border-radius: 9999px;
`;

const SubCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SubCardTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin: 0;
`;

const SubCardDesc = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.textSlate400};
  line-height: 1.625;
  margin: 12px 0 0 0;
`;

const SubCardLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.color || props.theme.mainColor};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// --- INTEROPERABLE PROVIDER SECTION ---
const CategoryGrid = styled.div`
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 48px;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const CategoryGridTitle = styled.h3`
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: ${props => props.theme.mainColor};
  margin-top: 0;
  margin-bottom: 24px;
`;

const CategoryBoxRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const CategoryBox = styled.div`
  background-color: ${props => props.theme.mainBg};
  border: 1px solid ${props => props.theme.secondaryBg};
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryEmoji = styled.span`
  font-size: 1.5rem;
`;

const CategoryLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.theme.textSlate300};
`;

const SplitFlexBox = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ColumnFlexLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeadingFlexWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeadingFlexIcon = styled.span`
  font-size: 1.875rem;
`;

const HeadingFlexTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 800;
  color: ${props => props.theme.secondaryBg};
  margin: 0;
`;

const BulletItem = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const BulletCircle = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color || props.theme.mainColor};
  margin-top: 6px;
  flex-shrink: 0;
`;

const BulletText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.secondaryColor};
  line-height: 1.625;
  margin: 0;
  
  strong {
    color: ${props => props.theme.secondaryBg};
  }
`;

const PromoActionBanner = styled.div`
  background: linear-gradient(to right, ${props => props.theme.mainColor}, ${props => props.theme.secondaryBg});
  border: 1px solid ${props => props.theme.secondaryBg};
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: center;

  @media (min-width: 1024px) {
    text-align: left;
  }
`;

const BannerActionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${props => props.theme.secondaryBg};
  margin: 0;
`;

const BannerActionDesc = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.mainBg};
  line-height: 1.625;
  margin: 0;
`;

const BannerButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;

  @media (min-width: 640px) {
    flex-direction: row;
  }
  @media (min-width: 1024px) {
    justify-content: flex-start;
  }
`;

const ActionBannerBtn = styled.a`
  background-color: ${props => props.theme.accentColor};
  color: ${props => props.theme.mainBg};
  font-size: 0.75rem;
  font-weight: 900;
  padding: 12px 24px;
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ActionOutlineBtn = styled.a`
  background-color: ${props => props.theme.secondaryBg};
  color: ${props => props.theme.accentColor};
  border: 1px solid ${props => props.theme.secondaryBg};
  font-size: 0.75rem;
  font-weight: 700;
  padding: 12px 24px;
  border-radius: 12px;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #1e293b;
  }
`;


// --- MAIN PORTAL COMPONENT ---
export default function About() {
  // Simulator States
  const [isConnected, setIsConnected] = useState(false);
  const [activeAddress, setActiveAddress] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0.00);
  const [totalSecondsStreamed, setTotalSecondsStreamed] = useState(0);
  const [totalDisbursed, setTotalDisbursed] = useState(0.00);
  
  // Controls States
  const [voucherInput, setVoucherInput] = useState("");
  const [paygValue, setPaygValue] = useState("");
  const [terminalLogs, setTerminalLogs] = useState([
    { text: "System Loading stream sandbox terminal...", type: "info" },
    { text: "System Ready. Click Play to emit XRPL ticks.", type: "info" }
  ]);
  const [toasts, setToasts] = useState([]);





 



 

  return (
    <PageWrapper>
      

      <AboutSection id="about">
        <AboutGrid>
          <AboutLeft>
            <AboutHeading>Web Monetization vs. <br />Legacy Ads</AboutHeading>
            <AboutDescPrimary>
              The current internet economy is broken. For decades, creators, artists, and writers have been forced to rely on advertising-driven networks or platform gatekeepers that take massive cuts of their hard-earned revenue. This ad-supported model has led to aggressive user-data tracking, intrusive pop-ups, and a compromised user experience.
            </AboutDescPrimary>
            <AboutDescSecondary>
              <strong>Swərl changes the rules of the game.</strong> By leveraging the speed, efficiency, and low cost of the XRP Ledger, Swərl provides a direct, frictionless bridge between creators and their audiences. Rather than using an ad-based revenue model, Swərl tracks the exact time spent consuming content, and sends payments directly to the content creator’s XRP wallet.
            </AboutDescSecondary>
          </AboutLeft>

          <AboutRight>
              {/* Pillars Grid */}
          
            <PillarCard>
              <PillarIconBox>⏱</PillarIconBox>
              <PillarTitle>Time-Based Tracking</PillarTitle>
              <PillarDesc>
                Swərl precisely measures the time (down to the second) you spend reading, listening, or watching content on supported platforms.
              </PillarDesc>
            </PillarCard>

            <PillarCard>
              <PillarIconBox bgColor="rgba(178, 102, 255, 0.1)" color={props => props.theme.secondaryColor}>💸</PillarIconBox>
              <PillarTitle>Direct Settlement</PillarTitle>
              <PillarDesc>
                Micropayments are calculated on the fly and dispatched directly to the content creator's self-custody or custodial XRP wallet.
              </PillarDesc>
            </PillarCard>

            <PillarCard>
              <PillarIconBox bgColor="rgba(5, 196, 131, 0.1)" color={props => props.theme.successColor}>🛡️</PillarIconBox>
              <PillarTitle>Zero Ad Exploitations</PillarTitle>
              <PillarDesc>
                Because your streaming activity directly funds the platform, there is no need for ad networks to track your behavior, mine your private data, or target you with cookies.
              </PillarDesc>
            </PillarCard>
          <SideBadgeCard>
              <FloatingBadgeIcon>🎯</FloatingBadgeIcon>
              <CardHeading>No Middlemen</CardHeading>
              <CardDesc>
                Traditional platforms capture up to 50% of revenue. Swərl leverages direct, programmatic XRPL payments to route fan support directly to artist accounts with near-zero ledger costs.
              </CardDesc>
              <CalloutBanner>
                <CalloutEmoji>🌱</CalloutEmoji>
                <CalloutLabel>Built to let creativity grow uninterrupted.</CalloutLabel>
              </CalloutBanner>
            </SideBadgeCard>
          </AboutRight>
        </AboutGrid>
      </AboutSection>

      {/* Subscription Pricing Catalog */}
      <PricingSection id="subscriptions">
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Sonar Muse Subscription Options</SectionTitle>
            <SectionDesc>
              <p>Access uninterrupted music, support global creators, and top up your account balance using the three native payment tracks.</p>
              <p>Instead of paying flat platform fees that rarely reach the creators you love, Swərl tracks your actual consumption in real-time.</p>
            </SectionDesc>
          </SectionHeader>

          <PricingGrid>
            {/* Track 1: Voucher Card */}
            <PricingCard ticket>
              <div>
                <CardHeaderRow>
                  <HeaderBadge style={{ color: props => props.theme.mainColor }}>Track 1</HeaderBadge>
                  <span style={{ fontSize: "1.5rem" }}>🎫</span>
                </CardHeaderRow>
                <CardHeading>Redeem Swərl Voucher</CardHeading>
                <CardDesc style={{ marginTop: "12px" }}>
                  Unlock full platform access using your on-chain digital collectibles. This option allows you to burn a Swərl NFT voucher directly from your connected wallet.
                </CardDesc>
                <DividerLine />
                <ListHeader>Highlights:</ListHeader>
                <FeatureList>
                  <li>Instant transaction signature</li>
                  <li>Gas-less on-chain confirmation</li>
                  <li>Fully portable and tradable assets</li>
                </FeatureList>
              </div>
              <OutlineButton onClick={() => {}}>
                Redeem $5 Voucher
              </OutlineButton>
            </PricingCard>

            {/* Track 2: Monthly Unlimited (Featured Option) */}
            <RecommendedCard>
              <RecommendBadge>Recommended</RecommendBadge>
              <div>
                <CardHeaderRow>
                  <HeaderBadge style={{ color: props => props.theme.mainColor }}>Track 2</HeaderBadge>
                  <span style={{ fontSize: "1.5rem" }}>👑</span>
                </CardHeaderRow>
                <CardHeading>Monthly Unlimited</CardHeading>
                <CardDesc style={{ marginTop: "12px" }}>
                  Our standard plan for daily listeners. Enjoy uninterrupted, high-quality music streaming across the library with maximum pricing discounts.
                </CardDesc>
                
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
                  <PriceOptionRow >
                    <OptionMetaBlock>
                      <OptionTitleText>1 Month Access</OptionTitleText>
                      <OptionDiscountText>Perfect for exploring</OptionDiscountText>
                    </OptionMetaBlock>
                    <OptionPriceText highlight>$5.00</OptionPriceText>
                  </PriceOptionRow>
                  <PriceOptionRow >
                    <OptionMetaBlock>
                      <OptionTitleText>3 Months Access</OptionTitleText>
                      <OptionDiscountText highlight>Save $1.00</OptionDiscountText>
                    </OptionMetaBlock>
                    <OptionPriceText highlight>$14.00</OptionPriceText>
                  </PriceOptionRow>
                  <PriceOptionRow>
                    <OptionMetaBlock>
                      <OptionTitleText>12 Months Access</OptionTitleText>
                      <OptionDiscountText style={{ color: props => props.theme.successColor }}>Save $4.00</OptionDiscountText>
                    </OptionMetaBlock>
                    <OptionPriceText>$56.00</OptionPriceText>
                  </PriceOptionRow>
                </div>
              </div>
              {/* <span style={{ fontSize: "10px", color: "#64748b", textAlign: "center", display: "block" }}>
                Includes offline listening & high-definition playback.
              </span> */}
            </RecommendedCard>

            {/* Track 3: Pay As You Go */}
            <PricingCard>
              <div>
                <CardHeaderRow>
                  <HeaderBadge style={{ color: props => props.theme.secondaryColor }}>Track 3</HeaderBadge>
                  <span style={{ fontSize: "1.5rem" }}>⚡</span>
                </CardHeaderRow>
                <CardHeading>Pay As You Go (PAYG)</CardHeading>
                <CardDesc style={{ marginTop: "12px" }}>
                  Flexible streaming fitting your lifestyle. Top up your account with any custom amount starting from just $1.00. Funds are only deducted while active audio is buffered.
                </CardDesc>
                <DividerLine />
                <ListHeader>Custom Recharge</ListHeader>
                <CompactInputGroup style={{ marginTop: "12px" }}>
                  <CompactInput 
                    type="number" 
                    value={paygValue}
                    onChange={(e) => setPaygValue(e.target.value)}
                    placeholder="Min. $1.00" 
                  />
                  <CompactApplyButton style={{ backgroundColor: props => props.theme.secondaryColor }}>
                    Load
                  </CompactApplyButton>
                </CompactInputGroup>
              </div>
              <span style={{ fontSize: "10px", color: "#64748b", textAlign: "center", display: "block" }}>
                Zero commitment. Total control.
              </span>
            </PricingCard>

            
            <SubCard>
              <div>
                <SubCardHeader>
                  <SubCardLabel>Track 1</SubCardLabel>
                  <span style={{ fontSize: "1.5rem" }}>🌱</span>
                </SubCardHeader>
                <SubCardTitle style={{ marginTop: "16px" }}>Direct Support</SubCardTitle>
                <SubCardDesc>
                  Every single second of your play-time directly payouts the exact creators you are consuming from. Your favorite musicians get paid per play.
                </SubCardDesc>
              </div>
              <SubCardLabel color={props => props.theme.textSlate500}>No platform cuts</SubCardLabel>
            </SubCard>

            <SubCardFeatured>
              <SubCardTag>Recommended</SubCardTag>
              <div>
                <SubCardHeader>
                  <SubCardLabel color="#df0909">Track 2</SubCardLabel>
                  <span style={{ fontSize: "1.5rem" }}>⚖️</span>
                </SubCardHeader>
                <SubCardTitle style={{ marginTop: "16px" }}>Value Integrity</SubCardTitle>
                <SubCardDesc style={{ color: props => props.theme.textSlate100 }}>
                  Your monthly fee is converted into streaming credits that flow steadily across the ledger, ensuring maximum payout efficiency and zero transaction lag.
                </SubCardDesc>
              </div>
              <SubCardLabel color={props => props.theme.mainColor}>Direct flow on-ledger</SubCardLabel>
            </SubCardFeatured>

            <SubCard>
              <div>
                <SubCardHeader>
                  <SubCardLabel>Track 3</SubCardLabel>
                  <span style={{ fontSize: "1.5rem" }}>🧩</span>
                </SubCardHeader>
                <SubCardTitle style={{ marginTop: "16px" }}>Composability</SubCardTitle>
                <SubCardDesc>
                  Because it lives on the XRPL, your subscription can seamlessly interact with digital store checkouts, high-res audio downloads, and artist collectibles on **Sonar Muse** and other compatible gateways.
                </SubCardDesc>
              </div>
              <SubCardLabel color={props => props.theme.textSlate500}>Web3 Native</SubCardLabel>
            </SubCard>
        
          </PricingGrid>
          
        </SectionContainer>
      </PricingSection>
    

      {/* 🌐 The Future: An Interoperable Web Monetization Provider */}
      <CardSection id="future">
        <SectionContainer>
          <CenterHeader>
            <SectionTitle>The Future: An Interoperable Provider</SectionTitle>
            <SectionDesc>
              Much like the foundational work laid out by services like <em>Coil</em>, Swərl's long-term goal is to expand far beyond music to become a global **Web Monetization Provider**.
            </SectionDesc>
          </CenterHeader>

          {/* Supported mediums grid */}
          <CategoryGrid>
            <CategoryGridTitle>Supporting All Digital Mediums</CategoryGridTitle>
            <CategoryBoxRow>
              <CategoryBox>
                <CategoryEmoji>📹</CategoryEmoji>
                <CategoryLabel>Video Creators</CategoryLabel>
              </CategoryBox>
              <CategoryBox>
                <CategoryEmoji>📸</CategoryEmoji>
                <CategoryLabel>Photographers</CategoryLabel>
              </CategoryBox>
              <CategoryBox>
                <CategoryEmoji>✍️</CategoryEmoji>
                <CategoryLabel>Writers</CategoryLabel>
              </CategoryBox>
              <CategoryBox>
                <CategoryEmoji>🎙️</CategoryEmoji>
                <CategoryLabel>Podcasters</CategoryLabel>
              </CategoryBox>
              <CategoryBox>
                <CategoryEmoji>🛠️</CategoryEmoji>
                <CategoryLabel>XRPL Projects</CategoryLabel>
              </CategoryBox>
            </CategoryBoxRow>
          </CategoryGrid>

          {/* ILP callout card */}
          <SplitFlexBox>
            <ColumnFlexLeft>
              <HeadingFlexWrapper>
                <HeadingFlexIcon>🔗</HeadingFlexIcon>
                <HeadingFlexTitle>Interledger Protocol (ILP) & Multi-Chain Expansion</HeadingFlexTitle>
              </HeadingFlexWrapper>
              <BodyDescPrimary>
                While the XRP Ledger serves as our secure high-speed base layer, Swərl is built with interoperability in mind. We are actively developing plans to incorporate the **Interledger Protocol (ILP)**.
              </BodyDescPrimary>
              <BodyDescSecondary>
                ILP integration will allow Swərl to bridge payments seamlessly across various independent ledgers. Fans will be able to fund their streaming allocations using their preferred digital assets, while creators will gain the flexibility to receive payments in their preferred multi-chain wallets (such as EVM networks like Soneium).
              </BodyDescSecondary>
            </ColumnFlexLeft>
            <ColumnFlexLeft>
              
              <BodyDescPrimary>
                Swərl is currently in active development as a selective prototype. We are strictly rolling this out in an artist-first sequence before expanding capabilities to the open web.
              </BodyDescPrimary>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <BulletItem>
                  <BulletCircle color={props => props.theme.mainColor} />
                  <BulletText>
                    <strong>Exclusive Beta Integration:</strong> To guarantee maximum security, stability, and utility, Swərl is currently only available to test natively on the Sonar Muse platform.
                  </BulletText>
                </BulletItem>
                <BulletItem>
                  <BulletCircle color={props => props.theme.secondaryColor} />
                  <BulletText>
                    <strong>Early Access Onboarding:</strong> We are focusing on gathering a premium catalog of pioneer artists and technical testers before scaling Web Monetization to the broader internet.
                  </BulletText>
                </BulletItem>
              </div>
            </ColumnFlexLeft>
          </SplitFlexBox>
        </SectionContainer>
      </CardSection>

      {/* 🛠️ Prototype status & Onboarding rollout */}
      <CardSection>
        <SectionContainer>
            {/* Promo Action Banner */}
            <PromoActionBanner>
              <BannerActionTitle>Join the Community</BannerActionTitle>
              <BannerActionDesc>
                Stay tuned for beta access giveaways, streaming credit drops, and real-time development updates from our engineering team.
              </BannerActionDesc>
              <BannerButtonRow>
                <ActionBannerBtn href="https://sonarmuse.com" target="_blank">
                  Explore Sonar Muse
                </ActionBannerBtn>
                <ActionOutlineBtn href="https://twitter.com/SonarMuse" target="_blank">
                  Follow @SonarMuse on X
                </ActionOutlineBtn>
              </BannerButtonRow>
            </PromoActionBanner>
        </SectionContainer>
      </CardSection>

    </PageWrapper>
  );
}
